// =============================================================================
// components/ux/RadialMenu.tsx
// R2 Radial Menu — hold RMB for configurable duration to show circular quick menu.
// 8-direction items, released direction = selected action.
// =============================================================================

import { useEffect, useRef, useState, useCallback, type JSX } from 'react';
import {
  Sparkles, FolderOpen, Mail, Settings, Camera,
  CalendarDays, Zap, Clipboard, Search,
} from 'lucide-react';
import { useUXControlStore } from '../../stores/uxControl.store.js';

interface RadialItem {
  id: string;
  label: string;
  icon: JSX.Element;
  angle: number; // degrees, 0 = right, 90 = down
  action: string;
  color: string;
}

const RADIAL_ITEMS: RadialItem[] = [
  { id: 'ai',         label: 'AI',          icon: <Sparkles size={16} />,    angle: 270, action: 'navigate:ai',               color: '#a78bfa' },
  { id: 'search',     label: 'Search',      icon: <Search size={16} />,       angle: 315, action: 'open-command-palette',      color: '#818cf8' },
  { id: 'project',    label: 'Projects',    icon: <FolderOpen size={16} />,   angle: 0,   action: 'navigate:projects',         color: '#5eead4' },
  { id: 'calendar',   label: 'Calendar',    icon: <CalendarDays size={16} />, angle: 45,  action: 'navigate:r2-calendar',      color: '#60a5fa' },
  { id: 'screenshot', label: 'Screenshot',  icon: <Camera size={16} />,       angle: 90,  action: 'screenshot',                color: '#4ade80' },
  { id: 'automation', label: 'Automation',  icon: <Zap size={16} />,          angle: 135, action: 'navigate:automation-studio', color: '#fbbf24' },
  { id: 'email',      label: 'Comms',       icon: <Mail size={16} />,         angle: 180, action: 'navigate:comms',            color: '#f472b6' },
  { id: 'clipboard',  label: 'Clipboard',   icon: <Clipboard size={16} />,    angle: 225, action: 'clipboard',                 color: '#fb923c' },
];

const RADIUS = 80;   // px from center to item center
const ITEM_SIZE = 48; // px

function degToRad(deg: number): number { return (deg * Math.PI) / 180; }

interface RadialMenuProps {
  onNavigate: (route: string) => void;
  onAction?: (action: string) => void;
  openCommandPalette?: () => void;
}

export function RadialMenu({ onNavigate, onAction, openCommandPalette }: RadialMenuProps): JSX.Element | null {
  const { radialMenuEnabled, radialHoldMs } = useUXControlStore();
  const [center, setCenter] = useState<{ x: number; y: number } | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);
  const [visible, setVisible] = useState(false);

  const show = useCallback((x: number, y: number) => {
    setCenter({ x, y });
    setVisible(true);
    setHoveredId(null);
  }, []);

  const hide = useCallback(() => {
    setVisible(false);
    setCenter(null);
    setHoveredId(null);
  }, []);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (e.button !== 2 || !radialMenuEnabled) return;
    mouseDownPos.current = { x: e.clientX, y: e.clientY };
    holdTimer.current = setTimeout(() => {
      show(e.clientX, e.clientY);
    }, radialHoldMs);
  }, [radialMenuEnabled, radialHoldMs, show]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (e.button !== 2) return;
    if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
  }, []);

  const handleContextMenu = useCallback((e: MouseEvent) => {
    if (!visible) return;
    // If radial is showing, block contextmenu so our radial handles the click
    e.preventDefault();
    e.stopPropagation();
  }, [visible]);

  useEffect(() => {
    if (!radialMenuEnabled) return;
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mouseup', handleMouseUp, true);
    document.addEventListener('contextmenu', handleContextMenu, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, [radialMenuEnabled, handleMouseDown, handleMouseUp, handleContextMenu]);

  const executeItem = useCallback((item: RadialItem) => {
    hide();
    const { action } = item;
    if (action.startsWith('navigate:')) { onNavigate(action.replace('navigate:', '')); return; }
    if (action === 'open-command-palette') { openCommandPalette?.(); return; }
    onAction?.(action);
  }, [hide, onNavigate, onAction, openCommandPalette]);

  if (!visible || !center) return null;

  return (
    <>
      {/* Backdrop to catch outside click */}
      <div
        onClick={hide}
        onContextMenu={(e) => { e.preventDefault(); hide(); }}
        style={{ position: 'fixed', inset: 0, zIndex: 99700 }}
      />

      {/* Radial container */}
      <div
        data-testid="radial-menu"
        style={{
          position: 'fixed',
          left: center.x - RADIUS - ITEM_SIZE / 2,
          top: center.y - RADIUS - ITEM_SIZE / 2,
          width: (RADIUS + ITEM_SIZE / 2) * 2,
          height: (RADIUS + ITEM_SIZE / 2) * 2,
          zIndex: 99750,
          pointerEvents: 'none',
        }}
      >
        {/* Center orb */}
        <div style={{
          position: 'absolute',
          left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 32, height: 32, borderRadius: '50%',
          background: 'rgba(37,181,168,0.15)',
          border: '2px solid rgba(37,181,168,0.5)',
          boxShadow: '0 0 20px rgba(37,181,168,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'radialPulse 1.5s ease-in-out infinite',
          pointerEvents: 'none',
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#5eead4' }} />
        </div>

        {/* Connection lines */}
        <svg
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
          width="100%" height="100%"
        >
          {RADIAL_ITEMS.map((item) => {
            const rad = degToRad(item.angle);
            const cx = (RADIUS + ITEM_SIZE / 2);
            const cy = (RADIUS + ITEM_SIZE / 2);
            const x2 = cx + Math.cos(rad) * RADIUS;
            const y2 = cy + Math.sin(rad) * RADIUS;
            return (
              <line
                key={item.id}
                x1={cx} y1={cy} x2={x2} y2={y2}
                stroke={item.id === hoveredId ? item.color : 'rgba(37,181,168,0.15)'}
                strokeWidth={item.id === hoveredId ? 1.5 : 1}
              />
            );
          })}
        </svg>

        {/* Menu items */}
        {RADIAL_ITEMS.map((item, i) => {
          const rad = degToRad(item.angle);
          const cx = RADIUS + ITEM_SIZE / 2;
          const cy = RADIUS + ITEM_SIZE / 2;
          const x = cx + Math.cos(rad) * RADIUS - ITEM_SIZE / 2;
          const y = cy + Math.sin(rad) * RADIUS - ITEM_SIZE / 2;
          const isHovered = hoveredId === item.id;

          return (
            <button
              key={item.id}
              type="button"
              data-testid={`radial-item-${item.id}`}
              onClick={(e) => { e.stopPropagation(); executeItem(item); }}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                position: 'absolute', left: x, top: y,
                width: ITEM_SIZE, height: ITEM_SIZE,
                borderRadius: '50%', cursor: 'pointer',
                background: isHovered ? `${item.color}1e` : 'rgba(3,8,22,0.9)',
                border: `2px solid ${isHovered ? item.color : 'rgba(37,181,168,0.25)'}`,
                boxShadow: isHovered ? `0 0 20px ${item.color}40` : '0 4px 16px rgba(0,0,0,0.5)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                color: isHovered ? item.color : 'rgba(148,172,210,0.7)',
                transition: 'all 0.15s',
                animation: `radialItemIn 0.2s cubic-bezier(0.34,1.56,0.64,1) ${i * 25}ms both`,
                pointerEvents: 'auto',
              }}
            >
              {item.icon}
              <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.06em', color: isHovered ? item.color : 'rgba(148,172,210,0.45)' }}>
                {item.label.toUpperCase()}
              </span>
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes radialItemIn {
          from { opacity: 0; transform: scale(0.4); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes radialPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(37,181,168,0.4); }
          50%       { box-shadow: 0 0 35px rgba(37,181,168,0.8); }
        }
      `}</style>
    </>
  );
}
